import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Modal,
  message,
  Tag,
  Card,
  Row,
  Col
} from 'antd';
import {
  SearchOutlined,
  UserAddOutlined,
  EditOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { userService, User } from '../../services/userService';
import UserModal from '../../components/users/UserModal';
import ResetPasswordModal from '../../components/users/ResetPasswordModal';
import styles from './UsersPage.module.css';

const { Search } = Input;

const UsersPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const queryClient = useQueryClient();

  // Fetch data
  const { data: usersData = { data: [], pagination: { total: 0, page: 1, limit: 20, totalPages: 0 } }, isLoading } = useQuery(
    ['users', searchText, pagination.current, pagination.pageSize],
    () => userService.getUsers({
      search: searchText,
      page: pagination.current,
      limit: pagination.pageSize
    }),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const users = usersData.data || [];

  // Mutations
  const createMutation = useMutation(
    (data: any) => userService.register(data),
    {
      onSuccess: () => {
        message.success('Tạo người dùng thành công!');
        queryClient.invalidateQueries('users');
        setModalVisible(false);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi tạo người dùng');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => userService.updateUser(id, data),
    {
      onSuccess: () => {
        message.success('Cập nhật người dùng thành công!');
        queryClient.invalidateQueries('users');
        setModalVisible(false);
        setEditingUser(null);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật người dùng');
      },
    }
  );

  const resetPasswordMutation = useMutation(
    ({ userId, newPassword }: { userId: number; newPassword: string }) => 
      userService.resetPassword(userId, newPassword),
    {
      onSuccess: () => {
        message.success('Reset mật khẩu thành công!');
        queryClient.invalidateQueries('users');
        setResetPasswordModalVisible(false);
        setSelectedUser(null);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi reset mật khẩu');
      },
    }
  );

  // Handlers
  const handleAdd = () => {
    setEditingUser(null);
    setModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setModalVisible(true);
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setResetPasswordModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingUser(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingUser) {
        await updateMutation.mutateAsync({ id: editingUser.id, data: values });
      } else {
        await createMutation.mutateAsync(values);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleResetPasswordSubmit = async (newPassword: string) => {
    if (!selectedUser) return;
    try {
      await resetPasswordMutation.mutateAsync({ userId: selectedUser.id, newPassword });
    } catch (error) {
      console.error('Reset password error:', error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'pharmacist':
        return 'blue';
      case 'assistant':
        return 'green';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Quản trị viên';
      case 'pharmacist':
        return 'Dược sĩ';
      case 'assistant':
        return 'Trợ lý';
      default:
        return role;
    }
  };

  const columns = [
    {
      title: 'Người dùng',
      key: 'user_info',
      width: 250,
      fixed: 'left' as const,
      sorter: (a: User, b: User) => a.username.localeCompare(b.username, 'vi'),
      render: (_: any, record: User) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {record.full_name}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            @{record.username}
          </div>
          {record.email && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              fontWeight: '400'
            }}>
              {record.email}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Vai trò',
      key: 'role',
      width: 120,
      render: (_: any, record: User) => (
        <Tag color={getRoleColor(record.role)}>
          {getRoleLabel(record.role)}
        </Tag>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 150,
      render: (_: any, record: User) => (
        <Space direction="vertical" size="small">
          <Tag color={record.is_active ? 'success' : 'error'}>
            {record.is_active ? 'Hoạt động' : 'Không hoạt động'}
          </Tag>
          {record.status && (
            <Tag color={record.status === 'active' ? 'green' : record.status === 'pending' ? 'orange' : 'red'}>
              {record.status === 'active' ? 'Đã kích hoạt' : record.status === 'pending' ? 'Chờ duyệt' : 'Vô hiệu'}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Ngày tạo',
      key: 'created_at',
      width: 150,
      sorter: (a: User, b: User) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      render: (_: any, record: User) => (
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          {dayjs(record.created_at).format('DD/MM/YYYY HH:mm')}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Sửa
          </Button>
          <Button
            type="link"
            icon={<KeyOutlined />}
            onClick={() => handleResetPassword(record)}
            size="small"
            danger
          >
            Reset MK
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <Card>
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Search
            placeholder="Tìm kiếm theo tên, username, email..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={handleSearch}
            style={{ width: '400px' }}
          />
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            size="large"
            onClick={handleAdd}
          >
            Thêm người dùng
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: usersData.pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Tổng ${total} người dùng`,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
        />
      </Card>

      <UserModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleSubmit}
        editingUser={editingUser}
        loading={createMutation.isLoading || updateMutation.isLoading}
      />

      <ResetPasswordModal
        visible={resetPasswordModalVisible}
        onCancel={() => {
          setResetPasswordModalVisible(false);
          setSelectedUser(null);
        }}
        onSubmit={handleResetPasswordSubmit}
        user={selectedUser}
        loading={resetPasswordMutation.isLoading}
      />
    </div>
  );
};

export default UsersPage;

